import React, { useEffect } from "react"
import {
  Box,
  FormControl,
  FormControlLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  SelectChangeEvent,
  TextField,
} from "@mui/material"
import dayjs, { Dayjs } from "dayjs"
import quarterOfYear from "dayjs/plugin/quarterOfYear"
import { DateRangeOutlined } from "@mui/icons-material"
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers"
import { DemoContainer } from "@mui/x-date-pickers/internals/demo"
import DateTimeWrapper from "../DateTimeWrapper/DateTimeWrapper"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import Popover from "@mui/material/Popover"
import {
  usePopupState,
  bindTrigger,
  bindPopover,
} from "material-ui-popup-state/hooks"
import { DateFilterLabels, DateFilterValues } from "../../constants/data"
import { DateRangeFormControl } from "./styles"

interface DateRangeSelectorProps {
  dateSelector: string
  filterState: {
    dateSelector: string
    startDate: Dayjs | null
    endDate: Dayjs | null
  }
  updateFilterState: (newState: {
    dateSelector: string
    startDate?: Dayjs | null
    endDate?: Dayjs | null
  }) => void
  disable?: boolean
}

const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  dateSelector,
  filterState,
  updateFilterState,
}) => {
  // Function to determine if the "Today" filter should prioritize yesterday's data
  function prioritizeTodayFilter() {
    if (
      filterState.dateSelector === DateFilterValues.TODAY &&
      filterState.startDate &&
      !dayjs().isSame(filterState.startDate, "day")
    ) {
      updateFilterState({
        dateSelector: DateFilterValues.YESTERDAY,
        startDate: dayjs().subtract(1, "day").startOf("day"),
        endDate: dayjs().subtract(1, "day").endOf("day"),
      })
    }
  }

  useEffect(() => {
    prioritizeTodayFilter()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterState])

  const popupState = usePopupState({
    variant: "popover",
    popupId: "dateFilterPopover",
  })

  const handleDateRangeChange = (event: SelectChangeEvent<string>) => {
    const {
      target: { value },
    } = event
    if (value !== DateFilterValues.CUSTOM) {
      popupState.close()
    }
    // Update startDate and endDate based on selected date range
    switch (value) {
      case DateFilterValues.TODAY:
        updateFilterState({
          dateSelector: DateFilterValues.TODAY,
          startDate: dayjs(),
          endDate: dayjs(),
        })
        break
      case DateFilterValues.TOMORROW:
        updateFilterState({
          dateSelector: DateFilterValues.TOMORROW,
          startDate: dayjs().add(1, "day"),
          endDate: dayjs().add(1, "day"),
        })
        break
      case DateFilterValues.YESTERDAY:
        updateFilterState({
          dateSelector: DateFilterValues.YESTERDAY,
          startDate: dayjs().subtract(1, "day"),
          endDate: dayjs().subtract(1, "day"),
        })
        break
      case DateFilterValues.SEVEN_DAYS:
        updateFilterState({
          dateSelector: DateFilterValues.SEVEN_DAYS,
          startDate: dayjs().subtract(7, "day"),
          endDate: dayjs(),
        })
        break
      case DateFilterValues.THRITY_DAYS:
        updateFilterState({
          dateSelector: DateFilterValues.THRITY_DAYS,
          startDate: dayjs().subtract(30, "day"),
          endDate: dayjs(),
        })
        break
      case DateFilterValues.WEEK:
        updateFilterState({
          dateSelector: DateFilterValues.WEEK,
          startDate: dayjs().startOf("week"),
          endDate: dayjs(),
        })
        break
      case DateFilterValues.MONTH:
        updateFilterState({
          dateSelector: DateFilterValues.MONTH,
          startDate: dayjs().startOf("month"),
          endDate: dayjs(),
        })
        break
      case DateFilterValues.LAST_MONTH:
        updateFilterState({
          dateSelector: DateFilterValues.LAST_MONTH,
          startDate: dayjs().subtract(1, "month").startOf("month"),
          endDate: dayjs().subtract(1, "month").endOf("month"),
        })
        break
      case DateFilterValues.QUARTER: {
        const currentMonth = dayjs().month()
        const quarterStartMonth = Math.floor(currentMonth / 3) * 3 // Calculate start month of current quarter
        const quarterStartDate = dayjs()
          .startOf("year")
          .month(quarterStartMonth) // Set start date to the calculated month
        updateFilterState({
          dateSelector: DateFilterValues.QUARTER,
          startDate: quarterStartDate,
          endDate: dayjs(),
        })
        break
      }
      case DateFilterValues.LAST_QUARTER: {
        dayjs.extend(quarterOfYear)
        const currentQuarter = dayjs().quarter() // Get the current quarter (1-4)
        let lastQuarterStartMonth = 0
        let lastQuarterEndMonth = 0
        let lastQuarterYear = dayjs().year() // Default to current year

        // Calculate the last quarter
        const lastQuarter = currentQuarter === 1 ? 4 : currentQuarter - 1 // If Q1, last quarter is Q4

        // Determine the start and end months for the last quarter
        switch (lastQuarter) {
          case 1:
            lastQuarterStartMonth = 0 // January
            lastQuarterEndMonth = 2 // March
            break
          case 2:
            lastQuarterStartMonth = 3 // April
            lastQuarterEndMonth = 5 // June
            break
          case 3:
            lastQuarterStartMonth = 6 // July
            lastQuarterEndMonth = 8 // September
            break
          case 4:
            lastQuarterStartMonth = 9 // October
            lastQuarterEndMonth = 11 // December
            lastQuarterYear -= 1 // Adjust year for the previous year
            break
        }

        // Calculate the start and end dates for the last quarter
        const lastQuarterStartDate = dayjs()
          .year(lastQuarterYear)
          .month(lastQuarterStartMonth)
          .startOf("month") // First day of the start month

        const lastQuarterEndDate = dayjs()
          .year(lastQuarterYear)
          .month(lastQuarterEndMonth)
          .endOf("month") // Last day of the end month

        updateFilterState({
          dateSelector: DateFilterValues.LAST_QUARTER,
          startDate: lastQuarterStartDate,
          endDate: lastQuarterEndDate,
        })
        break
      }
      case DateFilterValues.LAST_NINTY_DAYS: {
        updateFilterState({
          dateSelector: DateFilterValues.LAST_NINTY_DAYS,
          startDate: dayjs().subtract(90, "days"),
          endDate: dayjs(),
        })
        break
      }
      case DateFilterValues.CUSTOM:
        updateFilterState({
          dateSelector: DateFilterValues.CUSTOM,
        })
        break
    }
  }

  const renderCustomDatePickers = () => (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DemoContainer components={["DatePicker"]}>
        <DateTimeWrapper style={{ width: "100%" }}>
          <DatePicker
            label="Start Date"
            format="DD/MM/YYYY"
            value={filterState.startDate ? dayjs(filterState.startDate) : null}
            onChange={(newValue) =>
              updateFilterState({
                ...filterState,
                startDate: newValue,
              })
            }
            sx={{ width: "100%" }}
          />
        </DateTimeWrapper>
      </DemoContainer>
      <DemoContainer components={["DatePicker"]}>
        <DateTimeWrapper style={{ width: "100%" }}>
          <DatePicker
            label="End Date"
            format="DD/MM/YYYY"
            value={filterState.endDate ? dayjs(filterState.endDate) : null}
            onChange={(newValue) =>
              updateFilterState({
                ...filterState,
                endDate: newValue,
              })
            }
            sx={{ width: "100%" }}
          />
        </DateTimeWrapper>
      </DemoContainer>
    </LocalizationProvider>
  )

  const getDateFileterLabel = (value: string) => {
    if (value === DateFilterValues.CUSTOM) {
      return filterState.startDate && filterState.endDate
        ? `${dayjs(filterState.startDate).format("DD/MM/YYYY")} - ${dayjs(
            filterState.endDate,
          ).format("DD/MM/YYYY")}`
        : DateFilterValues.CUSTOM
    }
    return DateFilterLabels[value as DateFilterValues] || ""
  }

  return (
    <DateRangeFormControl size="small">
      <TextField
        {...bindTrigger(popupState)}
        size="small"
        id="date-range-id"
        value={getDateFileterLabel(dateSelector)}
        label="Date Range"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <DateRangeOutlined fontSize="small" />
            </InputAdornment>
          ),
          slotProps: {
            input: {
              readOnly: true,
              style: {
                cursor: "pointer",
              },
            },
          },
        }}
      ></TextField>
      <Popover
        {...bindPopover(popupState)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Box
          sx={{
            background: "white",
            padding: "16px",
            borderRadius: "4px",
            width: "250px",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          <FormControl size="small">
            <RadioGroup
              aria-labelledby="demo-radio-buttons-group-label"
              name="radio-buttons-group"
              value={dateSelector}
              onChange={handleDateRangeChange}
            >
              {Object.values(DateFilterValues).map((filterValue) => (
                <FormControlLabel
                  key={filterValue}
                  value={filterValue}
                  control={<Radio />}
                  label={DateFilterLabels[filterValue as DateFilterValues]}
                />
              ))}
            </RadioGroup>
          </FormControl>
          {dateSelector === DateFilterValues.CUSTOM &&
            renderCustomDatePickers()}
        </Box>
      </Popover>
    </DateRangeFormControl>
  )
}

export default DateRangeSelector
